import api from './axios';
import { logError } from '../common/utils/logger';
import { API_ENDPOINTS } from '../constants';
import { encryptJourneyQueryValue } from '../common/utils/journeyEncryption';

const RESUME_DOB_SESSION_KEY = 'rnlic_resumeDob';
const getResumeDobStorageKey = (applicationNumber) =>
  applicationNumber ? `${RESUME_DOB_SESSION_KEY}:${String(applicationNumber).trim()}` : RESUME_DOB_SESSION_KEY;

const readStoredResumeDob = (applicationNumber) => {
  try {
    const scopedKey = getResumeDobStorageKey(applicationNumber);
    const v =
      (applicationNumber ? sessionStorage.getItem(scopedKey) : null) ??
      sessionStorage.getItem(RESUME_DOB_SESSION_KEY);
    return (typeof v === 'string' && v.trim()) ? v.trim() : null;
  } catch (e) {
    return null;
  }
};

/**
 * Initialize quote by saving journey data and returning eligibility details
 * @param {Object} formData - Form data to submit
 * @param {string} [applicationNumber] - Optional application number (if updating existing journey)
 * @param {string} [caseId] - Optional case ID (if updating existing journey)
 * @returns {Promise<Object>} Quote init response with eligibility, defaults, and IDs
 */
export const initQuote = async (formData, applicationNumber = null, caseId = null) => {
  try {
    if (!formData || typeof formData !== 'object') {
      throw new Error('Quote init requires form data payload.');
    }

    // Build request payload with formData and optional IDs
    const requestPayload = { ...formData };
    if (applicationNumber) {
      requestPayload.applicationNumber = encryptJourneyQueryValue(applicationNumber);
      const effectiveDob = readStoredResumeDob(applicationNumber);
      if (effectiveDob) requestPayload.dateOfBirth = encryptJourneyQueryValue(effectiveDob);
    }
    if (caseId) {
      requestPayload.caseId = caseId;
    }

    const response = await api.post(API_ENDPOINTS.QUOTE_INIT, requestPayload);
    return response?.data ?? null;
  } catch (error) {
    logError('[initQuote] Failed to init quote', error);
    throw error;
  }
};

/**
 * Submit quote by updating case, updating journey, and creating quote record
 * @param {Object} quoteData - Quote form data to submit
 * @param {string} applicationNumber - Application number (required)
 * @param {string} caseId - Case ID (required)
 * @returns {Promise<Object>} Quote submission response
 */
export const submitQuote = async (quoteData, applicationNumber, caseId) => {
  try {
    if (!quoteData || typeof quoteData !== 'object') {
      throw new Error('Quote submission requires quote data payload.');
    }
    if (!applicationNumber) {
      throw new Error('Quote submission requires application number.');
    }
    if (!caseId) {
      throw new Error('Quote submission requires case ID.');
    }

    // Build request payload with quoteData and required IDs
    const effectiveDob = readStoredResumeDob(applicationNumber);
    const requestPayload = {
      ...quoteData,
      applicationNumber: encryptJourneyQueryValue(applicationNumber),
      caseId,
      ...(effectiveDob ? { dateOfBirth: encryptJourneyQueryValue(effectiveDob) } : {}),
    };

    const response = await api.post(API_ENDPOINTS.SUBMIT_QUOTE, requestPayload);
    return response?.data ?? null;
  } catch (error) {
    logError('[submitQuote] Failed to submit quote', error);
    throw error;
  }
};

/**
 * Update quote summary with BI API response data
 * Called when user initiates payment on the summary page
 * @param {string} applicationNumber - Application number (required)
 * @param {Object} quoteSummary - Quote summary data (BI API response) to store
 * @returns {Promise<Object>} Quote update response
 */
export const updateQuoteSummary = async (applicationNumber, quoteSummary) => {
  try {
    if (!applicationNumber) {
      throw new Error('Application number is required to update quote summary.');
    }
    if (!quoteSummary || typeof quoteSummary !== 'object') {
      throw new Error('Quote summary data is required.');
    }

    const effectiveDob = readStoredResumeDob(applicationNumber);
    const requestPayload = {
      applicationNumber: encryptJourneyQueryValue(applicationNumber),
      quoteSummary,
      ...(effectiveDob ? { dateOfBirth: encryptJourneyQueryValue(effectiveDob) } : {}),
    };

    const response = await api.post(API_ENDPOINTS.UPDATE_QUOTE_SUMMARY, requestPayload);
    return response?.data ?? null;
  } catch (error) {
    logError('[updateQuoteSummary] Failed to update quote summary', error);
    throw error;
  }
};
